import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Auth } from '@freelancer/auth';
import { Datastore } from '@freelancer/datastore';
import type { UserInteractionsCollection } from '@freelancer/datastore/collections';
import { ModalService, ModalSize } from '@freelancer/ui/modal';
import { UI_CONFIG } from '@freelancer/ui/ui.config';
import type { UiConfig } from '@freelancer/ui/ui.interface';
import { assertNever } from '@freelancer/utils';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EXCLUDED_URLS,
  LOADSHIFT_TERMS_AGREED_EVENT,
  LOADSHIFT_TERMS_DECLINED_EVENT,
  type LoadshiftTermsModalResult,
  LoadshiftTermsResponse,
} from './loadshift-terms-prompt.types';

@UntilDestroy({ className: 'LoadshiftTermsPromptService' })
@Injectable({
  providedIn: 'root',
})
export class LoadshiftTermsPromptService {
  private auth = inject(Auth);
  private datastore = inject(Datastore);
  private modalService = inject(ModalService);
  private platformId = inject(PLATFORM_ID);
  private uiConfig = inject<UiConfig>(UI_CONFIG);

  private isLoadshiftTermsPromptDisabled = false;

  // Only to be called by LoadshiftTermsPromptOverridesGuard
  setIsLoadshiftTermsPromptDisabled(disabled: boolean): void {
    this.isLoadshiftTermsPromptDisabled = disabled;
  }

  async shouldShowLoadshiftTermsPrompt(url: string): Promise<boolean> {
    if (
      this.isLoadshiftTermsPromptDisabled ||
      !isPlatformBrowser(this.platformId) ||
      this.uiConfig.theme !== 'freightlancer' ||
      this.isExcludedUrl(url)
    ) {
      return false;
    }

    const isLoggedIn = await firstValueFrom(
      this.auth.isLoggedIn().pipe(untilDestroyed(this)),
    );
    if (!isLoggedIn) {
      return false;
    }

    const [hasAgreed, hasDeclined] = await Promise.all([
      this.hasResponded(LoadshiftTermsResponse.AGREED),
      this.hasResponded(LoadshiftTermsResponse.DECLINED),
    ]);

    return !hasAgreed && !hasDeclined;
  }

  /**
   * Opens the terms modal. The user has to choose to agree or decline, and
   * the modal tries to save their response before closing.
   *
   * @returns the user's response and whether it was saved, or undefined if
   * the modal was closed without one (e.g. on navigation).
   */
  async openTermsModal(): Promise<LoadshiftTermsModalResult | undefined> {
    const result = await this.modalService
      .open('FreightlancerTermsModal', {
        size: ModalSize.MID,
        mobileFullscreen: false,
        closeable: false,
        // Back navigates instead of only closing the modal
        preventBackNavigation: true,
      })
      .afterClosed();

    return Object.values(LoadshiftTermsResponse).includes(result?.response)
      ? result
      : undefined;
  }

  /** @returns true if the response was saved */
  async recordResponse(response: LoadshiftTermsResponse): Promise<boolean> {
    if (await this.hasResponded(response)) {
      return true;
    }

    const result = await this.datastore
      .collection<UserInteractionsCollection>('userInteractions')
      .push({ eventName: this.getEventName(response) });

    return result.status === 'success';
  }

  async hasAgreed(): Promise<boolean> {
    return this.hasResponded(LoadshiftTermsResponse.AGREED);
  }

  protected async hasResponded(
    response: LoadshiftTermsResponse,
  ): Promise<boolean> {
    return firstValueFrom(
      this.datastore
        .collection<UserInteractionsCollection>('userInteractions', query =>
          query.where('eventName', '==', this.getEventName(response)),
        )
        .valueChanges()
        .pipe(
          map(interactions => interactions.length > 0),
          untilDestroyed(this),
        ),
    );
  }

  private getEventName(response: LoadshiftTermsResponse): string {
    switch (response) {
      case LoadshiftTermsResponse.AGREED:
        return LOADSHIFT_TERMS_AGREED_EVENT;
      case LoadshiftTermsResponse.DECLINED:
        return LOADSHIFT_TERMS_DECLINED_EVENT;
      default:
        return assertNever(response);
    }
  }

  private isExcludedUrl(url: string): boolean {
    return EXCLUDED_URLS.some(
      excludedUrl =>
        url.startsWith(excludedUrl) ||
        url.startsWith(`/freightlancer${excludedUrl}`),
    );
  }
}
